import React, { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Shield, AlertCircle, CheckCircle, Search } from 'lucide-react';

interface BidVisibility {
  bid_id: string;
  insurer_id: string;
  insurer_name: string;
  amount: number;
  status: string;
  created_at: string;
  is_visible: boolean;
  visibility_reason: string;
  user_role: string;
  user_id: string;
  request_owner: string;
  request_status: string;
  request_bid_status: string;
}

interface BidCheck {
  check_name: string;
  check_passed: boolean;
  details: string;
}

export function BidDiagnostics() {
  const [requestId, setRequestId] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [visibility, setVisibility] = useState<BidVisibility[]>([]);
  const [checks, setChecks] = useState<BidCheck[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = async () => {
    if (!referenceNumber) return;
    
    setLoading(true);
    setError(null);
    try {
      // First get the request ID from the reference number
      const { data: requests, error: requestError } = await supabase
        .from('insurance_requests')
        .select('id')
        .eq('reference_number', referenceNumber)
        .single();

      if (requestError) throw requestError;
      if (!requests) throw new Error('Request not found');

      setRequestId(requests.id);
      await runDiagnostics(requests.id);
    } catch (err) {
      console.error('Search error:', err);
      setError(err instanceof Error ? err.message : 'Failed to find request');
      setLoading(false);
    }
  };

  const runDiagnostics = async (id: string) => {
    try {
      // Run both diagnostic functions in parallel
      const [visibilityResult, checksResult] = await Promise.all([
        supabase.rpc('diagnose_bid_visibility', { p_request_id: id }),
        supabase.rpc('verify_bid_data', { p_request_id: id })
      ]);

      if (visibilityResult.error) throw visibilityResult.error;
      if (checksResult.error) throw checksResult.error;

      setVisibility(visibilityResult.data || []);
      setChecks(checksResult.data || []);
    } catch (err) {
      console.error('Diagnostic error:', err);
      setError(err instanceof Error ? err.message : 'Failed to run diagnostics');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center mb-4">
            <Shield className="w-6 h-6 text-indigo-600 mr-2" />
            Bid Diagnostics
          </h2>
          <div className="flex items-center space-x-4">
            <div className="relative flex-1 max-w-xs">
              <input
                type="text"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
                placeholder="Enter reference number..."
                className="w-full pl-10 pr-4 py-2 border rounded-md"
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            </div>
            <button
              onClick={handleSearch}
              disabled={!referenceNumber || loading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              Search
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      ) : (
        <>
          {/* Data Integrity Checks */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Data Integrity Checks</h3>
              {checks.length === 0 ? (
                <div className="text-center py-4 text-gray-500">
                  No diagnostic data available
                </div>
              ) : (
                <div className="space-y-4">
                  {checks.map((check, index) => (
                    <div 
                      key={index}
                      className={`p-4 rounded-lg ${
                        check.check_passed ? 'bg-green-50' : 'bg-red-50'
                      }`}
                    >
                      <div className="flex items-center">
                        {check.check_passed ? (
                          <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                        )}
                        <div>
                          <h4 className="font-medium text-gray-900">{check.check_name}</h4>
                          <p className="text-sm text-gray-600">{check.details}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Bid Visibility */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Bid Visibility</h3>
              {visibility.length === 0 ? (
                <div className="text-center py-4 text-gray-500">
                  No bids found for this request
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Insurer
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Visibility
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {visibility.map((bid) => (
                        <tr key={bid.bid_id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {bid.insurer_name}
                            </div>
                            <div className="text-sm text-gray-500">
                              ID: {bid.insurer_id}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              £{bid.amount.toLocaleString('en-GB', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              bid.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {bid.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(bid.created_at).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                bid.is_visible ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {bid.is_visible ? 'Visible' : 'Hidden'}
                              </span>
                              <span className="ml-2 text-sm text-gray-500">
                                ({bid.visibility_reason})
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Request Details */}
          {visibility[0] && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Request Details</h3>
                <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Request Owner</dt>
                    <dd className="mt-1 text-sm text-gray-900">{visibility[0].request_owner}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Request Status</dt>
                    <dd className="mt-1 text-sm text-gray-900">{visibility[0].request_status}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Bid Status</dt>
                    <dd className="mt-1 text-sm text-gray-900">{visibility[0].request_bid_status}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Current User</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      Role: {visibility[0].user_role}
                      <br />
                      ID: {visibility[0].user_id}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}